import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, Space, Tag } from "antd";
import { AxiosError } from "axios";
import Highlighter from "react-highlight-words";

import { IRole } from "../../interfaces";
import RolesService from "../../services/http/services/RolesService";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableRoleData extends IRole {
    key: number;
}

export default function useRoles() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITableRoleData>>([]);
    const [roleBeingDeleted, setRoleBeingDeleted] = useState<ITableRoleData | null>(null);

    const [searchText, setSearchText] = useState<string>("");
    const [filterWithPermissions, setFilterWithPermissions] = useState<"ALL" | "WITH" | "WITHOUT">("ALL");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const navigate = useNavigate();

    const loadRoles = useCallback(async (signal?: AbortSignal) => {
        try {
            const controller = signal ? undefined : new AbortController();
            const roles = await RolesService.listRoles(signal || controller!.signal);
            const mappedData: ITableRoleData[] = (roles || []).map((role: IRole) => ({
                ...role,
                key: role.id || Math.random(),
            }));
            setTableData(mappedData);
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }
            EmitError(err, true, "roles_load_error");
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);
        loadRoles(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadRoles]);

    const handleDeleteRole = useCallback((data: ITableRoleData) => {
        setRoleBeingDeleted(data);
        setIsDeleteModalVisible(true);
    }, []);

    const handleClickEditRole = useCallback(
        (data: ITableRoleData) => {
            navigate(`/configuracoes/perfis/editar/${data.id}`);
        },
        [navigate]
    );

    const handleClickCreateNewRole = useCallback(() => {
        navigate("/configuracoes/perfis/criar");
    }, [navigate]);

    const handleConfirmDeleteRole = useCallback(async () => {
        if (!roleBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await RolesService.deleteRole(roleBeingDeleted.id);
            EmitSuccessAlert("Perfil de acesso removido com sucesso!");
            setTableData((prev) => prev.filter((item) => item.id !== roleBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setRoleBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, "roles_delete_error");
        } finally {
            setIsLoadingDelete(false);
        }
    }, [roleBeingDeleted]);

    const handleCancelDeleteRole = useCallback(() => {
        setIsDeleteModalVisible(false);
        setRoleBeingDeleted(null);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setSearchText("");
        setFilterWithPermissions("ALL");
        setCurrentPage(1);
    }, []);

    const filteredData = useMemo(() => {
        let result = tableData;
        if (searchText && searchText.trim()) {
            const query = searchText.toLowerCase().trim();
            result = result.filter(
                (role) =>
                    role.name?.toLowerCase().includes(query) ||
                    role.description?.toLowerCase().includes(query)
            );
        }

        if (filterWithPermissions === "WITH") {
            result = result.filter((role) => role.permissions && role.permissions.length > 0);
        } else if (filterWithPermissions === "WITHOUT") {
            result = result.filter((role) => !role.permissions || role.permissions.length === 0);
        }

        return result;
    }, [tableData, searchText, filterWithPermissions]);

    const activeFilterItems = useMemo((): IActiveFilterItem[] => {
        const items: IActiveFilterItem[] = [];
        if (searchText.trim()) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${searchText}"`,
                onClear: () => {
                    setSearchText("");
                    setCurrentPage(1);
                },
            });
        }
        if (filterWithPermissions !== "ALL") {
            items.push({
                key: "perms_filter",
                label: "Permissões",
                value: filterWithPermissions === "WITH" ? "Com permissões" : "Sem permissões",
                onClear: () => {
                    setFilterWithPermissions("ALL");
                    setCurrentPage(1);
                },
            });
        }
        return items;
    }, [searchText, filterWithPermissions]);

    const stats = useMemo(() => {
        const total = tableData.length;
        const withPerms = tableData.filter((r) => r.permissions && r.permissions.length > 0).length;
        const totalPermsCount = tableData.reduce((acc, r) => acc + (r.permissions?.length || 0), 0);
        return {
            total,
            withPerms,
            totalPermsCount,
        };
    }, [tableData]);

    const columns = useMemo(
        () => [
            {
                title: "Perfil",
                dataIndex: "name",
                key: "name",
                sorter: (a: ITableRoleData, b: ITableRoleData) =>
                    (a.name || "").localeCompare(b.name || ""),
                render: (text: string) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={searchText ? [searchText] : []}
                                autoEscape
                                textToHighlight={text || "--"}
                            />
                        </span>
                    </div>
                ),
            },
            {
                title: "Descrição",
                dataIndex: "description",
                key: "description",
                sorter: (a: ITableRoleData, b: ITableRoleData) =>
                    (a.description || "").localeCompare(b.description || ""),
                render: (text: string) => (
                    <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12.5 }}>
                        {text ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={searchText ? [searchText] : []}
                                autoEscape
                                textToHighlight={text}
                            />
                        ) : (
                            <span style={{ color: isDark ? "#595959" : "#94a3b8", fontStyle: "italic" }}>
                                Sem descrição
                            </span>
                        )}
                    </span>
                ),
            },
            {
                title: "Permissões Atribuídas",
                dataIndex: "permissions",
                key: "permissions",
                width: 220,
                sorter: (a: ITableRoleData, b: ITableRoleData) =>
                    (a.permissions?.length || 0) - (b.permissions?.length || 0),
                render: (_: any, record: ITableRoleData) => {
                    const perms = record.permissions || [];
                    if (perms.length === 0) {
                        return (
                            <Tag color="default" style={{ borderRadius: 10, margin: 0, fontSize: 11.5 }}>
                                Nenhuma permissão
                            </Tag>
                        );
                    }

                    const popoverContent = (
                        <div
                            style={{
                                maxWidth: 320,
                                maxHeight: 240,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 600,
                                    fontSize: 12,
                                    marginBottom: 4,
                                    color: isDark ? "#f0f0f0" : "#1e293b",
                                }}
                            >
                                {perms.length} Permiss{perms.length === 1 ? "ão" : "ões"} vinculada{perms.length === 1 ? "" : "s"}:
                            </div>
                            {perms.map((p) => (
                                <div
                                    key={p.id || p.name}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        fontSize: 12,
                                        padding: "4px 8px",
                                        background: isDark ? "#1f1f1f" : "#f8fafc",
                                        borderRadius: 4,
                                        border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}`,
                                        color: isDark ? "#d4d4d4" : "#334155",
                                    }}
                                >
                                    <span style={{ color: "#722ed1", fontWeight: 600 }}>•</span>
                                    <span>{p.description || p.name}</span>
                                </div>
                            ))}
                        </div>
                    );

                    return (
                        <Popover
                            content={popoverContent}
                            title={null}
                            trigger="hover"
                            placement="topLeft"
                        >
                            <Tag
                                color="purple"
                                style={{
                                    borderRadius: 10,
                                    margin: 0,
                                    fontWeight: 600,
                                    fontSize: 11.5,
                                    cursor: "pointer",
                                }}
                            >
                                {perms.length} permiss{perms.length === 1 ? "ão" : "ões"}
                            </Tag>
                        </Popover>
                    );
                },
            },
            {
                title: "Ações",
                key: "actions",
                width: 100,
                align: "center" as const,
                render: (_: any, record: ITableRoleData) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Space size={6}>
                            <TableActionButton
                                action="edit"
                                tooltip="Editar perfil"
                                onClick={() => handleClickEditRole(record)}
                            />
                            <TableActionButton
                                action="delete"
                                tooltip="Excluir perfil"
                                onClick={() => handleDeleteRole(record)}
                            />
                        </Space>
                    </div>
                ),
            },
        ],
        [isDark, searchText, handleClickEditRole, handleDeleteRole]
    );

    return {
        isLoading,
        isRefreshing,
        isDeleteModalVisible,
        isLoadingDelete,
        roleBeingDeleted,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        filterWithPermissions,
        setFilterWithPermissions,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        stats,
        activeFilterItems,
        handleClickCreateNewRole,
        handleClickEditRole,
        handleConfirmDeleteRole,
        handleCancelDeleteRole,
        handleClearAllFilters,
    };
}
